import { Injectable } from "@angular/core";
import {
  AssetDataService,
  AssetTypeImageService,
  AssetTypeResourcesService,
  AssetTypesService,
  LiveAssetFilters,
  LiveAssetPropertiesService,
  LiveAssetResourcesService,
  LiveAssetsService,
  LiveAssetStatus,
  PropertiesService,
  PropertyCreation
} from "@rvi/asset-modules";
import { catchError, forkJoin, map, mergeMap, Observable, of } from "rxjs";
import { ConfigurationItem } from "../../_objects/tenant-config";
import { TenantConfigService } from "../http-api/tenant-config.service";
import { LabelConfig, LiveAssetMapper } from "./live-asset.mapper";

@Injectable({
  providedIn: "root"
})
export class AssetModuleClient {
  constructor(
    private liveAssetsService: LiveAssetsService,
    private assetTypeImageService: AssetTypeImageService,
    private assetTypesService: AssetTypesService,
    private liveAssetPropertiesService: LiveAssetPropertiesService,
    private propertiesService: PropertiesService,
    private tenantConfigService: TenantConfigService,
    private assetDataService: AssetDataService,
    private assetTypeResourcesService: AssetTypeResourcesService,
    private liveAssetResourcesService: LiveAssetResourcesService
  ) {}

  public getLiveAssets(tenantId: string, filters?: LiveAssetFilters) {
    return this.liveAssetsService.getAll(filters).pipe(
      mergeMap((lives) => {
        const subjects$ = lives.items.map((live) => LiveAssetMapper.fromLiveAssetToSubject(live, tenantId));
        return subjects$.length > 0 ? forkJoin(subjects$) : of([]);
      })
    );
  }

  public getLiveAsset(id: string, tenantId: string) {
    return this.liveAssetsService
      .getLiveAssetDetails(id)
      .pipe(
        mergeMap((live) =>
          LiveAssetMapper.fromLiveAssetDetailsToSubject(live, tenantId, (assetTypeId) => this.assetTypeImageService.getImage(assetTypeId))
        )
      );
  }

  public getLiveAssetAsSubject(id: string, tenantId: string) {
    return forkJoin({
      live: this.liveAssetsService.getLiveAssetDetails(id),
      labelConfig: this.buildLabelConfig()
    }).pipe(
      mergeMap(({ live, labelConfig }) =>
        LiveAssetMapper.fromLiveAssetDetailsToSubject(
          live,
          tenantId,
          (assetTypeId) => this.assetTypeImageService.getImage(assetTypeId),
          (liveAssetId) => this.liveAssetPropertiesService.getAll(liveAssetId),
          labelConfig
        )
      )
    );
  }

  public getLiveAssetDetails(id: string) {
    return this.liveAssetsService.getLiveAssetDetails(id);
  }

  private buildLabelConfig(): Observable<LabelConfig> {
    const get = (configs: ConfigurationItem[], key: string) => configs.find((c) => c.key === key)?.value as string | undefined;
    return this.tenantConfigService.getTenantConfigs().pipe(
      catchError(() => of([])),
      map((configs) => ({
        Owner: get(configs, "LabelOfOwner"),
        Country: get(configs, "LabelOfCountry"),
        State: get(configs, "LabelOfState"),
        Region: get(configs, "LabelOfRegion"),
        Site: get(configs, "LabelOfSite"),
        Project: get(configs, "LabelOfProject")
      }))
    );
  }

  public importAssetType(file: File) {
    return this.assetTypesService.importAssetType(file);
  }

  public createLiveAsset(assetTypeId: string) {
    return this.liveAssetsService.createLiveAsset(assetTypeId);
  }

  public setIsDeletable(liveAssetId: string, isDeletable: boolean) {
    return this.liveAssetsService.setIsDeletable(liveAssetId, isDeletable);
  }

  public getAssetTypes() {
    return this.assetTypesService.getAssetTypes();
  }

  public getLiveAssetProperties(liveAssetId: string) {
    return this.liveAssetPropertiesService.getAll(liveAssetId);
  }

  public updateLiveAssetProperties(liveAssetId: string, properties: { id: string; value: string }[]) {
    return this.liveAssetPropertiesService.updateProperties(liveAssetId, properties);
  }

  public createLiveAssetProperties(liveAssetId: string, propertiesIds: string[]) {
    return this.liveAssetPropertiesService.createProperties(liveAssetId, propertiesIds);
  }

  public updateLiveAssetStatus(liveAssetId: string, status: LiveAssetStatus, applyToAllChildren: boolean) {
    return this.liveAssetsService.updateLiveAssetStatus(liveAssetId, status, applyToAllChildren);
  }

  public createProperties(properties: PropertyCreation[]) {
    return this.propertiesService.createProperties(properties);
  }

  public getProperties() {
    return this.propertiesService.getAll();
  }

  public deleteAssetDataBundle(): Observable<void> {
    return this.assetDataService.deleteAssetDataBundle();
  }

  public validateStatus(id: string, status: LiveAssetStatus) {
    return this.liveAssetsService.validateUpdateStatus(id, status);
  }

  public getAssetTypeResources(assetTypeId: string) {
    return this.assetTypeResourcesService.getAssetTypeResources(assetTypeId);
  }

  public getLiveAssetResources(liveAssetId: string) {
    return this.liveAssetResourcesService.getLiveAssetResources(liveAssetId);
  }
}
